import { MessageCircle, X } from "lucide-react";
import { buildSms } from "@/lib/sms";
import { useLang } from "@/contexts/LangContext";
import { useSettings } from "@/contexts/SettingsContext";

// Bottom-sheet that lists sub-items for a category. Each item is a quick
// text-to-order shortcut that pre-fills the SMS body with the item name +
// tracking ref. Closes on backdrop click or X.

export const CategorySheet = ({ open, onClose, category }) => {
  const { t } = useLang();
  const { items: itemsByCat } = useSettings();
  if (!category) return null;
  const items = itemsByCat[category.id] || [];

  return (
    <div
      data-testid="category-sheet"
      className={`fixed inset-0 z-[80] transition ${open ? "pointer-events-auto" : "pointer-events-none"}`}
      aria-hidden={!open}
    >
      {/* Backdrop */}
      <button
        aria-label="Close"
        onClick={onClose}
        className={`absolute inset-0 bg-black/70 backdrop-blur-sm transition-opacity ${
          open ? "opacity-100" : "opacity-0"
        }`}
      />

      {/* Sheet */}
      <div
        className={`absolute inset-x-0 bottom-0 max-h-[88vh] overflow-y-auto rounded-t-3xl border-t border-[#00ff41]/30 bg-[#0a0a0a] p-5 sm:p-8 transition-transform duration-300 ${
          open ? "translate-y-0" : "translate-y-full"
        }`}
      >
        <div className="mx-auto max-w-3xl">
          {/* Drag handle */}
          <div className="mx-auto mb-4 h-1 w-12 rounded-full bg-white/20" />

          {/* Header */}
          <div className="flex items-start justify-between gap-4 mb-5">
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#00ff41]">
                {category.name}
              </p>
              <h3 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight">
                {category.blurb}
              </h3>
              <p className="mt-2 text-sm text-neutral-400 max-w-md">
                {t("sheet_pricing_note")}
              </p>
            </div>
            <button
              data-testid="sheet-close-btn"
              onClick={onClose}
              className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-white/15 text-white hover:bg-white/10"
              aria-label={t("sheet_close")}
            >
              <X size={16} />
            </button>
          </div>

          {/* Item grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {items.map((item) => (
              <a
                key={item}
                data-testid={`sheet-item-${item.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                href={buildSms({ categoryId: category.id, itemName: item })}
                className="group flex items-center justify-between gap-3 rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3 hover:border-[#00ff41]/60 hover:bg-[#00ff41]/5 transition"
              >
                <span className="font-display font-semibold">{item}</span>
                <span className="inline-flex items-center gap-1.5 rounded-full bg-[#00ff41]/15 px-2.5 py-1 text-[11px] font-display font-bold text-[#00ff41] group-hover:bg-[#00ff41] group-hover:text-black transition">
                  <MessageCircle size={11} />
                  {t("sheet_cta")}
                </span>
              </a>
            ))}
          </div>

          {/* Footer CTA */}
          <a
            data-testid="sheet-all-btn"
            href={buildSms({ categoryId: category.id })}
            className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#00ff41] px-5 py-3.5 font-display font-bold text-black hover:bg-[#00e63a] transition"
          >
            <MessageCircle size={16} />
            {t("sheet_all")}
          </a>
        </div>
      </div>
    </div>
  );
};

export default CategorySheet;
